import React, { useEffect, useRef, useState } from "react";

const BG_URL = "https://customer-assets.emergentagent.com/job_client-360-pro/artifacts/4wwxk1jl_ChatGPT%20Image%205_06_2026%2C%2021_58_33.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-360-pro/artifacts/t5motboh_wertia%20perfect.png";

/**
 * Cinematic splash intro for Wertia Finanz CRM.
 * - 5 seconds total visible time (matches piano theme audio length)
 * - Soft fade-in (logo + tagline rise), then fade-out
 * - Piano theme audio: best-effort autoplay (browsers may block on first ever visit;
 *   on subsequent visits with media engagement, audio plays automatically).
 *   A page-level "first interaction" listener also tries to unlock audio
 *   if user clicks anywhere on the document before intro completes.
 * - Skippable by clicking anywhere
 * - Shown once per browser session (sessionStorage flag)
 */
export default function WertiaIntro({ onDone }) {
  const [phase, setPhase] = useState("enter"); // enter | hold | exit
  const audioRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;

    const tryPlay = () => {
      if (!a) return Promise.resolve();
      if (!a.paused) return Promise.resolve();
      a.volume = 0.6;
      const p = a.play();
      if (p && typeof p.catch === "function") {
        return p.catch(() => { /* still blocked, ignore */ });
      }
      return Promise.resolve();
    };

    // Strategy 1: immediate best-effort (works for sites with high MEI)
    tryPlay();
    // Strategy 2: retry as soon as the media is ready to play
    const onCanPlay = () => tryPlay();
    if (a) a.addEventListener("canplay", onCanPlay, { once: true });
    // Strategy 3: any user interaction during intro unlocks audio
    const onInteract = () => tryPlay();
    const opts = { once: true, capture: true };
    document.addEventListener("pointerdown", onInteract, opts);
    document.addEventListener("keydown", onInteract, opts);
    document.addEventListener("touchstart", onInteract, opts);
    document.addEventListener("mousemove", onInteract, opts);

    const t1 = setTimeout(() => setPhase("hold"), 100);
    const t2 = setTimeout(() => setPhase("exit"), 4400);
    const t3 = setTimeout(() => {
      try { sessionStorage.setItem("wertia_intro_seen", "1"); } catch { /* */ }
      onDone && onDone();
    }, 5000);

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (a) a.removeEventListener("canplay", onCanPlay);
      document.removeEventListener("pointerdown", onInteract, { capture: true });
      document.removeEventListener("keydown", onInteract, { capture: true });
      document.removeEventListener("touchstart", onInteract, { capture: true });
      document.removeEventListener("mousemove", onInteract, { capture: true });
      if (a) { try { a.pause(); } catch { /* */ } }
    };
  }, [onDone]);

  const skip = () => {
    setPhase("exit");
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* */ }
    }
    setTimeout(() => {
      try { sessionStorage.setItem("wertia_intro_seen", "1"); } catch { /* */ }
      onDone && onDone();
    }, 350);
  };

  return (
    <div
      onClick={skip}
      className={`fixed inset-0 z-[100] flex items-center justify-center cursor-pointer transition-opacity duration-500 ${
        phase === "exit" ? "opacity-0" : "opacity-100"
      }`}
      style={{
        backgroundImage: `linear-gradient(rgba(20,16,14,0.55), rgba(20,16,14,0.85)), url("${BG_URL}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "#141010",
      }}
      data-testid="wertia-intro"
    >
      {/* Piano theme audio — autoplay attribute + JS fallback strategies for browser policies */}
      <audio
        ref={audioRef}
        preload="auto"
        autoPlay
        data-testid="intro-audio"
      >
        <source src="/intro/wertia-intro.mp3" type="audio/mpeg" />
        <source src="/intro/wertia-intro.ogg" type="audio/ogg" />
      </audio>

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.65) 100%)",
      }} />

      {/* Subtle gold grain glow behind logo */}
      <div
        className={`absolute w-[480px] h-[480px] rounded-full pointer-events-none transition-all duration-[1800ms] ${
          phase === "enter" ? "opacity-0 scale-90" : "opacity-100 scale-100"
        }`}
        style={{
          background: "radial-gradient(circle, rgba(223,184,100,0.18) 0%, transparent 65%)",
        }}
      />

      <div className="relative flex flex-col items-center text-center px-6">
        {/* Logo */}
        <img
          src={LOGO_URL}
          alt="Wertia Finanz"
          className={`w-44 md:w-56 lg:w-64 select-none drop-shadow-[0_8px_32px_rgba(223,184,100,0.45)] transition-all duration-[1400ms] ease-out ${
            phase === "enter" ? "opacity-0 translate-y-4 scale-95" : "opacity-100 translate-y-0 scale-100"
          }`}
          draggable={false}
          data-testid="intro-logo"
        />

        {/* Brand wordmark */}
        <div
          className={`mt-6 font-display font-extrabold tracking-[0.18em] text-white text-3xl md:text-4xl transition-all duration-[1400ms] delay-200 ease-out ${
            phase === "enter" ? "opacity-0 translate-y-3" : "opacity-100 translate-y-0"
          }`}
          style={{ textShadow: "0 4px 24px rgba(0,0,0,0.6)" }}
        >
          WERTIA <span className="text-[#DFB864]">FINANZ</span>
        </div>

        {/* Tagline */}
        <div
          className={`mt-3 text-[10px] md:text-xs uppercase tracking-[0.45em] text-white/60 transition-all duration-[1400ms] delay-500 ease-out ${
            phase === "enter" ? "opacity-0" : "opacity-100"
          }`}
        >
          Patrimonial Advisory · Switzerland
        </div>

        {/* Thin gold accent line */}
        <div
          className={`mt-7 h-px bg-gradient-to-r from-transparent via-[#DFB864] to-transparent transition-all duration-[1600ms] delay-700 ease-out ${
            phase === "enter" ? "w-0 opacity-0" : "w-48 opacity-90"
          }`}
        />
      </div>

      {/* Skip hint */}
      <div className={`absolute bottom-6 right-6 text-[10px] uppercase tracking-widest text-white/30 transition-opacity duration-700 ${
        phase === "hold" ? "opacity-100" : "opacity-0"
      }`}>
        Click to skip
      </div>
    </div>
  );
}
